import React, { useEffect, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Visibility, Download, Edit, Delete } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import CustomDialog from '../../../layout/CustomDialog';
export default function ArquivosTab({
    modulo: m,
    documento,
    permissoes: p,
    parametro,
    isMobile,
    cabecalhoPendente,
    somenteConsulta = false,
}) {
    const mrt = useMRTState({
        storageKey: somenteConsulta ? `ArquivoDocumento.Consulta.${documento.uuid}` : 'ArquivoDocumento.Arquivos',
        pageSizeDefault: Number(parametro?.paginacao) || 5,
    });
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    const columns = useMemo(
        () => [
            {
                accessorKey: 'nomeOriginal',
                header: 'Arquivo',
                Cell: ({ row }) => row.original.nomeOriginal || row.original.arquivo,
            },
            {
                id: 'origemRedacao',
                header: 'Origem',
                accessorFn: (r) =>
                    r.origemRedacao === 'emitido'
                        ? 'Editor — emitido'
                        : r.origemRedacao === 'assinado'
                          ? 'Editor — assinado/externo'
                          : 'Anexo',
            },
            { accessorKey: 'descricao', header: 'Descrição', muiTableBodyCellProps: { sx: { whiteSpace: 'normal' } } },
            {
                id: 'apenasSocio',
                header: 'Acesso',
                accessorFn: (row) =>
                    [true, 1, '1', 'true'].includes(row.apenasSocio) ? 'Apenas sócios' : 'Permissões da rotina',
            },
            {
                accessorKey: 'tamanho',
                header: 'Tamanho',
                Cell: ({ cell }) =>
                    cell.getValue() == null
                        ? ''
                        : `${(Number(cell.getValue()) / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`,
            },
            {
                accessorKey: 'createdAt',
                header: 'Incluído em',
                Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleString('pt-BR') : ''),
            },
        ],
        []
    );
    useEffect(() => {
        const ultima = Math.max(0, Math.ceil(m.lista.length / mrt.pagination.pageSize) - 1);
        if (mrt.pagination.pageIndex > ultima) mrt.setPagination((prev) => ({ ...prev, pageIndex: ultima }));
    }, [m.lista.length, mrt.pagination.pageIndex, mrt.pagination.pageSize, mrt.setPagination]);
    if (!documento.uuid)
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                Salve o cabeçalho para anexar os arquivos. Você pode usar “Salvar e anexar”.
            </Alert>
        );
    const podeEditarDescricao = !somenteConsulta && (m.editando ? p.editarItem : m.podeAnexar);
    const gerenciar = !somenteConsulta && p.editarItem;
    return (
        <Stack spacing={2} sx={{ py: 2, minWidth: 0 }}>
            {cabecalhoPendente && <Alert severity="info">Salve o cabeçalho antes de trabalhar com os arquivos.</Alert>}
            {!somenteConsulta && !documento.ativo && (
                <Alert severity="info">Documento inativo: inclusão de novos anexos indisponível.</Alert>
            )}
            {m.erro && <Alert severity="error">{m.erro}</Alert>}
            {!somenteConsulta && m.lista.some((a) => a.redacaoId) && (
                <Alert severity="info">
                    Os arquivos criados pelo editor são preservados com a redação. Para criar outra versão ou excluir, use a aba
                    Editor.
                </Alert>
            )}
            {!p.imprimirItem && (
                <Alert severity="info">A abertura e o download dos anexos exigem a permissão Imprimir item.</Alert>
            )}
            {m.carregando && <Typography>Carregando arquivos...</Typography>}
            {gerenciar && (
                <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Button component="label" variant="outlined" disabled={m.ocupado || !m.podeAnexar || Boolean(m.editando)}>
                            Selecionar arquivos
                            <input
                                hidden
                                type="file"
                                multiple
                                accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
                                disabled={m.ocupado || !m.podeAnexar || Boolean(m.editando)}
                                onChange={m.selecionarArquivos}
                            />
                        </Button>
                        <Typography variant="caption">
                            PDF, JPG, PNG, GIF ou WEBP. Até 10 arquivos, com até 15 MB cada.
                        </Typography>
                    </Stack>
                    {m.files.length > 0 && (
                        <Box>
                            {m.files.map((f, i) => (
                                <Typography key={`${f.name}-${i}`} variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                    {f.name}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    <TextField
                        label={m.editando ? 'Descrição do anexo selecionado' : 'Descrição dos novos anexos (opcional)'}
                        value={m.descricao}
                        disabled={m.ocupado || !podeEditarDescricao}
                        onChange={(e) => m.alterarDescricao(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 255 }}
                    />
                    <FormControlLabel
                        label="Apenas sócios"
                        title="Restringe o acesso a sócios com vínculo ativo e vigente na empresa do documento"
                        control={
                            <Checkbox
                                name="apenasSocio"
                                checked={m.apenasSocio === true}
                                disabled={m.ocupado || !podeEditarDescricao}
                                onChange={(e) => m.alterarApenasSocio(e.target.checked)}
                            />
                        }
                    />
                    <Typography variant="caption">
                        {m.editando
                            ? 'A restrição se aplica ao anexo selecionado.'
                            : 'A restrição se aplica a todos os arquivos deste envio.'}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button
                            type="button"
                            variant="contained"
                            disabled={m.ocupado || !podeEditarDescricao || (!m.editando && !m.files.length)}
                            onClick={m.salvar}
                        >
                            {m.editando ? 'Salvar anexo' : 'Enviar arquivos'}
                        </Button>
                        <Button type="button" disabled={m.ocupado || !m.pendente} onClick={m.limpar}>
                            Descartar edição
                        </Button>
                    </Stack>
                </>
            )}
            <Button type="button" sx={{ alignSelf: 'flex-start' }} disabled={m.ocupado} onClick={m.recarregar}>
                Atualizar lista
            </Button>
            {!somenteConsulta && p.cancelarItem && m.selecionado && (
                <CustomDialog
                    title="Excluir anexo"
                    content={`Excluir o anexo ${m.selecionado.nomeOriginal || m.selecionado.arquivo}?`}
                    onClose={() => {
                        if (!m.ocupado) m.setSelecionado(null);
                    }}
                    onConfirm={m.excluir}
                />
            )}
            <MaterialReactTable
                {...defaults}
                columns={columns}
                data={m.lista}
                getRowId={(row) => row.uuid}
                enableRowSelection={false}
                manualFiltering={false}
                manualSorting={false}
                manualPagination={false}
                rowCount={undefined}
                autoResetPageIndex={true}
                enableGlobalFilterModes={false}
                renderRowActions={
                    isMobile
                        ? undefined
                        : ({ row }) => (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <IconButton
                                      type="button"
                                      title="Visualizar"
                                      disabled={m.ocupado || !p.imprimirItem}
                                      onClick={() => m.abrir(row.original)}
                                  >
                                      <Visibility />
                                  </IconButton>
                                  <IconButton
                                      type="button"
                                      title="Baixar"
                                      disabled={m.ocupado || !p.imprimirItem}
                                      onClick={() => m.abrir(row.original, true)}
                                  >
                                      <Download />
                                  </IconButton>
                                  {gerenciar && (
                                      <IconButton
                                          type="button"
                                          title="Editar anexo"
                                          disabled={m.ocupado || m.pendente || !p.editarItem || Boolean(row.original.redacaoId)}
                                          onClick={() => m.editar(row.original)}
                                      >
                                          <Edit />
                                      </IconButton>
                                  )}
                                  {!somenteConsulta && p.cancelarItem && (
                                      <IconButton
                                          type="button"
                                          title="Excluir"
                                          color="error"
                                          disabled={m.ocupado || m.pendente || !p.cancelarItem || Boolean(row.original.redacaoId)}
                                          onClick={() => m.setSelecionado(row.original)}
                                      >
                                          <Delete />
                                      </IconButton>
                                  )}
                              </Box>
                          )
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) =>
                              [
                                  <MenuItem
                                      key="abrir"
                                      disabled={m.ocupado || !p.imprimirItem}
                                      onClick={() => {
                                          closeMenu();
                                          m.abrir(row.original, false, { novaAba: true });
                                      }}
                                  >
                                      Visualizar
                                  </MenuItem>,
                                  <MenuItem
                                      key="baixar"
                                      disabled={m.ocupado || !p.imprimirItem}
                                      onClick={() => {
                                          closeMenu();
                                          m.abrir(row.original, true);
                                      }}
                                  >
                                      Baixar
                                  </MenuItem>,
                                  gerenciar && (
                                      <MenuItem
                                          key="editar"
                                          disabled={m.ocupado || m.pendente || !p.editarItem || Boolean(row.original.redacaoId)}
                                          onClick={() => {
                                              closeMenu();
                                              m.editar(row.original);
                                          }}
                                      >
                                          Editar anexo
                                      </MenuItem>
                                  ),
                                  !somenteConsulta && p.cancelarItem && (
                                      <MenuItem
                                          key="excluir"
                                          disabled={m.ocupado || m.pendente || !p.cancelarItem || Boolean(row.original.redacaoId)}
                                          onClick={() => {
                                              closeMenu();
                                              m.setSelecionado(row.original);
                                          }}
                                      >
                                          Excluir
                                      </MenuItem>
                                  ),
                              ].filter(Boolean)
                        : undefined
                }
            />
            <Dialog open={Boolean(m.preview)} onClose={m.fecharPreview} fullWidth maxWidth="lg" fullScreen={isMobile}>
                <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{m.preview?.nome || 'Documento'}</DialogTitle>
                <DialogContent sx={{ height: '75vh', p: 1 }}>
                    {m.preview?.mimeType === 'application/pdf' ? (
                        isMobile ? (
                            <Alert severity="info">
                                O PDF é aberto em outra aba. Se ela não abriu, toque em Abrir PDF. Você também pode baixar o
                                arquivo para abri-lo no leitor do celular.
                            </Alert>
                        ) : (
                            <iframe
                                title="Visualização do documento"
                                src={m.preview.url}
                                style={{ border: 0, width: '100%', height: '100%' }}
                            />
                        )
                    ) : m.preview?.mimeType?.startsWith('image/') ? (
                        <Box
                            component="img"
                            src={m.preview.url}
                            alt={m.preview.nome}
                            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Alert severity="info">Visualização indisponível para este formato. Utilize Baixar.</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {m.preview?.mimeType === 'application/pdf' && (
                        <Button component="a" href={m.preview.url} target="_blank" rel="noopener noreferrer">
                            Abrir PDF
                        </Button>
                    )}
                    {m.preview && (
                        <Button component="a" href={m.preview.url} download={m.preview.nome}>
                            Baixar
                        </Button>
                    )}
                    <Button type="button" onClick={m.fecharPreview}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
